export const AUTH_ERROR_PATH = '/auth/error';

/**
 * FFH-1918 / FFH-1927: sanitised reason codes passed to /auth/error. The raw
 * `error_description` never appears in the redirect URL; the error page
 * derives its copy from this code alone, avoiding reflected-content issues.
 *
 * Kept separate from on-callback.ts so the client-side error page can import
 * these without dragging next/server into the browser bundle.
 */
export const AUTH_ERROR_REASONS = {
  verifyEmail: 'verify-email',
  loginCancelled: 'login-cancelled',
  sessionLost: 'session-lost',
  stateMismatch: 'state-mismatch',
  codeExchangeFailed: 'code-exchange-failed',
  unknown: 'unknown'
} as const;

export type AuthErrorReason =
  (typeof AUTH_ERROR_REASONS)[keyof typeof AUTH_ERROR_REASONS];

export const isAuthErrorReason = (value: unknown): value is AuthErrorReason =>
  Object.values(AUTH_ERROR_REASONS).includes(value as AuthErrorReason);

/**
 * Denials the user brought about themselves. These log at warn level and are
 * excluded from the dashboard's unexpected-failures count.
 */
const EXPECTED_AUTH_ERROR_REASONS: ReadonlySet<AuthErrorReason> = new Set([
  AUTH_ERROR_REASONS.verifyEmail,
  AUTH_ERROR_REASONS.loginCancelled
]);

export const isExpectedAuthErrorReason = (reason: AuthErrorReason): boolean =>
  EXPECTED_AUTH_ERROR_REASONS.has(reason);

/**
 * Reasons where Auth0's own guidance is "try logging in again from the
 * application": the user usually still holds a live Auth0 session, so a
 * fresh login completes silently (FFH-2636).
 */
export const isRecoverableAuthErrorReason = (
  reason: AuthErrorReason
): boolean => reason === AUTH_ERROR_REASONS.sessionLost;

const ACCESS_DENIED_CODE = 'access_denied';
const INVALID_STATE_CODE = 'invalid_state';
const CODE_EXCHANGE_FAILED_CODE = 'authorization_code_grant_error';

// The Auth0 post-login action denies unverified users with "Please verify
// your email address before logging in". Match loosely so copy tweaks in the
// action don't silently downgrade the state to login-cancelled.
const EMAIL_VERIFICATION_PATTERN = /verif/i;

// Auth0's tenant error page sends two descriptions for a lost login session:
// "You may have pressed the back button, refreshed during login, opened too
// many login dialogs, or there is some issue with cookies, since we couldn't
// find your session" and "This URL can be used only once". Matched loosely
// on the distinctive fragments so an Auth0 copy tweak does not silently turn
// the auto-restart off (FFH-2636).
const SESSION_LOST_PATTERN =
  /couldn.t find your session|could not find your session|pressed the back button|can be used only once/i;

// Universal Login reports a user backing out as `user_cancelled_authorize`.
// Matched loosely for the same reason as verification, but it has to be a
// positive match: `access_denied` is also what Auth0 sends for its own
// refusals. In the 7 days to 3 Sep 2026, 42 of 53 callbacks that had been
// filed as login-cancelled carried the description "Forbidden" and 6 more a
// generic Auth0 failure; only 5 were a cancel (FFH-2860). Anything that is
// not clearly the user's choice stays unknown so it keeps reporting as a
// fault.
const LOGIN_CANCELLED_PATTERN = /cancel/i;

/**
 * Maps OAuth error params to a reason code. `access_denied` covers the two
 * expected denials (unverified email, user backed out of Universal Login);
 * a lost-session description is recoverable; a state mismatch or failed code
 * exchange get their own codes so the logs are facetable; every other code,
 * and an `access_denied` that is neither expected denial, is unknown and
 * treated as unexpected.
 */
export const deriveReasonFromOAuthError = (
  errorCode: string | undefined,
  errorDescription: string | undefined
): AuthErrorReason => {
  if (SESSION_LOST_PATTERN.test(errorDescription ?? '')) {
    return AUTH_ERROR_REASONS.sessionLost;
  }
  if (errorCode === ACCESS_DENIED_CODE) {
    const description = errorDescription ?? '';
    if (EMAIL_VERIFICATION_PATTERN.test(description)) {
      return AUTH_ERROR_REASONS.verifyEmail;
    }
    if (LOGIN_CANCELLED_PATTERN.test(description)) {
      return AUTH_ERROR_REASONS.loginCancelled;
    }
    return AUTH_ERROR_REASONS.unknown;
  }
  if (errorCode === INVALID_STATE_CODE) {
    return AUTH_ERROR_REASONS.stateMismatch;
  }
  if (errorCode === CODE_EXCHANGE_FAILED_CODE) {
    return AUTH_ERROR_REASONS.codeExchangeFailed;
  }
  return AUTH_ERROR_REASONS.unknown;
};
